"use client";

import * as React from "react";
import { Car, Search, Banknote, ShieldCheck, Wrench, RefreshCw, Settings2, CircleCheck, ArrowRight } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
const servicesData = [{
  id: 1,
  title: "Landscape Design",
  description: "Bespoke architectural planning that harmonizes your outdoor space with your home's aesthetic.",
  iconName: "Search",
  subService1: "3D Terrain Modeling",
  subService2: "Planting Palette Selection",
  subService3: "Hardscape Integration",
  priceInfo: "Consultation Included"
}, {
  id: 2,
  title: "Custom Patios & Fencing",
  description: "Premium stone masonry and artisanal fencing to define your property's boundaries with style.",
  iconName: "Car",
  subService1: "Natural Stone Pavers",
  subService2: "Privacy Fence Installation",
  subService3: "Structural Retaining Walls",
  priceInfo: "Custom Quote"
}, {
  id: 3,
  title: "Irrigation & Maintenance",
  description: "Smart watering systems and precision lawn care to keep your landscape thriving year-round.",
  iconName: "Settings2",
  subService1: "Drip System Repair",
  subService2: "Seasonal Fertilization",
  subService3: "Lawn Pest Control",
  priceInfo: "Monthly Plans Available"
}, {
  id: 4,
  title: "Aesthetic Flower Painting",
  description: "Artistic floral arrangements and seasonal color displays that elevate your garden's visual impact.",
  iconName: "ShieldCheck",
  subService1: "Perennial Bed Design",
  subService2: "Seasonal Flower Rotation",
  subService3: "Mulching & Soil Health",
  priceInfo: "Starting from $299"
}, {
  id: 5,
  title: "Power Washing & Gutters",
  description: "Deep cleaning services to restore the pristine look of your walkways, siding, and drainage.",
  iconName: "Wrench",
  subService1: "Pressure Surface Cleaning",
  subService2: "Debris Gutter Removal",
  subService3: "Soft Wash Roof Care",
  priceInfo: "Flat Rate Options"
}, {
  id: 6,
  title: "Hedge & Tree Pruning",
  description: "Expert arboriculture and precision trimming to maintain the health and shape of your greenery.",
  iconName: "RefreshCw",
  subService1: "Structural Tree Pruning",
  subService2: "Hedge Sculpting",
  subService3: "Fall & Spring Cleanup",
  priceInfo: "Free Consultation"
}];
const iconMap: Record<string, React.ElementType> = {
  Car,
  Search,
  Banknote,
  ShieldCheck,
  Wrench,
  RefreshCw,
  Settings2
};
export default function OurServices() {
  return <section data-section-id="224697" id="specialized-property-care" className="dark relative w-full py-24 bg-background text-foreground overflow-hidden z-0">
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[100px] -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-primary/5 rounded-lg blur-[120px] -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <div className="inline-flex items-center justify-center p-2 bg-primary/20 rounded-lg mb-2">
            <Settings2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Specialized Property Care
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl font-default">
            Campos V Landscaping delivers organic precision—from high-end landscape design to 
            meticulous maintenance. Hover over our services to explore our craft.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, i) => {
          const IconComponent = iconMap[service.iconName] || Car;
          return <HoverCard key={service.id} openDelay={100} closeDelay={200}>
                <HoverCardTrigger asChild data-index={i}>
                  <div tabIndex={0} className="group relative flex flex-col h-full bg-card/40 backdrop-blur-xl border border-border/40 rounded-2xl p-8 cursor-pointer transition-all duration-500 hover:bg-card/80 hover:border-primary/40 hover:shadow-2xl hover:shadow-black/20 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary">
                    {}
                    <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-lg bg-background/60 border border-border/40 text-muted-foreground group-hover:text-primary group-hover:border-primary/40 transition-colors duration-300">
                      <IconComponent className="w-7 h-7" strokeWidth={1.2} />
                    </div>

                    <h3 className="text-xl font-default font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                      {service.title}
                    </h3>
                    
                    <p className="text-muted-foreground font-default leading-relaxed flex-grow">
                      {service.description}
                    </p>

                    <div className="mt-6 flex items-center text-sm font-medium text-primary/90 group-hover:text-primary transition-colors duration-300">
                      <span>View Service Details</span>
                      <ArrowRight className="w-4 h-4 ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    </div>
                  </div>
                </HoverCardTrigger>

                <HoverCardContent side="bottom" align="start" className="w-80 p-0 overflow-hidden border-border/60 shadow-3xl shadow-black/40 bg-popover/98 backdrop-blur-2xl rounded-lg">
                  <div className="bg-muted/40 px-5 py-4 border-b border-border/50">
                    <h4 className="font-default font-semibold text-foreground flex items-center gap-2">
                      <IconComponent className="w-4 h-4 text-primary" strokeWidth={2} />
                      Service Scope
                    </h4>
                  </div>
                  <div className="p-5 space-y-4">
                    <ul className="space-y-3">
                      {[service.subService1, service.subService2, service.subService3].map((sub, idx) => <li key={idx} className="flex items-start gap-3 text-sm font-default text-muted-foreground">
                            <CircleCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                            <span>{sub}</span>
                          </li>)}
                    </ul>
                    <div className="pt-4 mt-4 border-t border-border/50 flex justify-between items-center">
                      <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                        Pricing
                      </span>
                      <span className="text-sm font-bold text-primary-foreground bg-primary px-2 py-1 rounded-md">
                        {service.priceInfo}
                      </span>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>;
        })}
        </div>
      </div>
    </section>;
}